import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from './useAuth';

export interface ClockedInWorker {
  id: string;
  resource_id: string;
  clock_in: string;
  rate_amount?: number | null;
  break_start?: string | null;
  break_end?: string | null;
  resources?: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string | null;
    hourly_rate?: number | null;
    compensation_type?: string | null;
  } | null;
  resource?: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string | null;
    hourly_rate?: number | null;
    compensation_type?: string | null;
  } | null;
}

/** Live clock earnings: elapsed paid time × hourly rate (minus active/completed break). */
export function calculateLiveClockEarnings(
  clockIn: string,
  hourlyRate: number,
  now: Date,
  opts?: { breakStart?: string | null; breakEnd?: string | null }
): number {
  if (!hourlyRate || hourlyRate <= 0) return 0;

  let elapsedMs = now.getTime() - new Date(clockIn).getTime();
  if (opts?.breakStart) {
    const breakStartMs = new Date(opts.breakStart).getTime();
    const breakEndMs = opts.breakEnd ? new Date(opts.breakEnd).getTime() : now.getTime();
    elapsedMs -= Math.max(0, breakEndMs - breakStartMs);
  }

  const hours = Math.max(0, elapsedMs / (1000 * 60 * 60));
  return hours * hourlyRate;
}

export function getWorkerHourlyRate(entry: ClockedInWorker): number {
  if (entry.rate_amount != null && entry.rate_amount > 0) {
    return Number(entry.rate_amount);
  }
  const resource = entry.resources || entry.resource;
  const type = resource?.compensation_type ?? 'hourly';
  if (type !== 'hourly') return 0;
  return Number(resource?.hourly_rate) || 0;
}

export interface WorkerActiveJob {
  resource_id: string;
  booking_id: string;
  booking_number?: number;
  booking_title?: string | null;
}

/** A fix older than this is "stale": the phone has gone quiet. */
export const LOCATION_STALE_AFTER_MS = 10 * 60 * 1000;

export type WorkerLocationStatus = 'live' | 'stale' | 'none';

export interface WorkerLocationSnapshot {
  activity?: string | null;
  speed?: number | null;
  odometer?: number | null;
  lat?: number | null;
  lng?: number | null;
  /** When the phone captured the fix (gps_timestamp, else created_at). */
  at?: string | null;
}

export function locationStatus(
  location: WorkerLocationSnapshot | undefined,
  now: Date,
): WorkerLocationStatus {
  if (!location || location.lat == null || location.lng == null || !location.at) return 'none';
  const age = now.getTime() - new Date(location.at).getTime();
  return age <= LOCATION_STALE_AFTER_MS ? 'live' : 'stale';
}

function dedupeClockedIn(entries: ClockedInWorker[]): ClockedInWorker[] {
  return entries.reduce((acc: ClockedInWorker[], entry) => {
    const existing = acc.find((e) => e.resource_id === entry.resource_id);
    if (!existing) {
      acc.push(entry);
    } else if (new Date(entry.clock_in) > new Date(existing.clock_in)) {
      acc[acc.indexOf(existing)] = entry;
    }
    return acc;
  }, []);
}

export function useActiveWorkers() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const businessId = selectedBusiness?.id;

  const [workers, setWorkers] = useState<ClockedInWorker[]>([]);
  const [activeJobs, setActiveJobs] = useState<WorkerActiveJob[]>([]);
  const [locations, setLocations] = useState<Map<string, WorkerLocationSnapshot>>(new Map());
  const [canView, setCanView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const fetchLocations = useCallback(
    async (resourceIds: string[]) => {
      if (!supabase || !businessId || resourceIds.length === 0) {
        setLocations(new Map());
        return;
      }

      const results = await Promise.all(
        resourceIds.map(async (resourceId) => {
          const { data } = await supabase
            .from('location_updates')
            .select('resource_id, activity, speed, odometer, lat, lng, gps_timestamp, created_at')
            .eq('business_id', businessId)
            .eq('resource_id', resourceId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          return data ? ([resourceId, data] as const) : null;
        })
      );

      const map = new Map<string, WorkerLocationSnapshot>();
      for (const row of results) {
        if (row) {
          const [, loc] = row;
          map.set(row[0], {
            activity: loc.activity,
            speed: loc.speed,
            odometer: loc.odometer,
            lat: loc.lat,
            lng: loc.lng,
            at: loc.gps_timestamp || loc.created_at,
          });
        }
      }
      setLocations(map);
    },
    [supabase, businessId]
  );

  const load = useCallback(async () => {
    if (!supabase || !businessId || !user) {
      setWorkers([]);
      setActiveJobs([]);
      setLocations(new Map());
      setCanView(false);
      setLoading(false);
      return;
    }

    try {
      const { data: business } = await supabase
        .from('businesses')
        .select('owner_id')
        .eq('id', businessId)
        .single();

      const isBusinessOwner = business?.owner_id === user.id;

      const { data: resource } = await supabase
        .from('resources')
        .select('is_admin')
        .eq('auth_user_id', user.id)
        .eq('business_id', businessId)
        .maybeSingle();

      const isAdminResource = resource?.is_admin === true;
      const allowed = isBusinessOwner || isAdminResource;
      setCanView(allowed);

      if (!allowed) {
        setWorkers([]);
        setActiveJobs([]);
        setLocations(new Map());
        return;
      }

      const { data: clockedInData, error: clockError } = await supabase
        .from('payroll_page')
        .select(
          `
          id,
          resource_id,
          clock_in,
          rate_amount,
          break_start,
          break_end,
          resources (
            first_name,
            last_name,
            avatar_url,
            hourly_rate,
            compensation_type
          )
        `
        )
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .is('clock_out', null)
        .order('clock_in', { ascending: false });

      if (clockError) {
        console.error('Error fetching active workers:', clockError);
        return;
      }

      const deduped = dedupeClockedIn((clockedInData || []) as ClockedInWorker[]);
      setWorkers(deduped);

      const resourceIds = deduped.map((w) => w.resource_id);
      await fetchLocations(resourceIds);

      const { data: jobEntries } = await supabase
        .from('job_time_entries')
        .select('id, resource_id, booking_id, started_at')
        .eq('business_id', businessId)
        .is('stopped_at', null);

      if (jobEntries?.length) {
        const bookingIds = [...new Set(jobEntries.map((e) => e.booking_id))];
        const { data: bookings } = await supabase
          .from('bookings')
          .select('id, booking_number, title')
          .in('id', bookingIds);

        const bookingMap = new Map((bookings || []).map((b) => [b.id, b]));
        setActiveJobs(
          jobEntries.map((e) => {
            const b = bookingMap.get(e.booking_id);
            return {
              resource_id: e.resource_id,
              booking_id: e.booking_id,
              booking_number: b?.booking_number,
              booking_title: b?.title,
            };
          })
        );
      } else {
        setActiveJobs([]);
      }
    } catch (err) {
      console.error('Error loading active workers:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, user, fetchLocations]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!canView || workers.length === 0) return;
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, [canView, workers.length]);

  useEffect(() => {
    if (!supabase || !businessId || !canView) return;

    const channel = supabase
      .channel(`active-workers-${businessId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payroll_page' },
        () => {
          load();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [supabase, businessId, canView, load]);

  // Phones post a row to location_updates every few seconds while clocked in;
  // fold each one in so the "sharing" indicator is live, not 10 s behind.
  useEffect(() => {
    if (!supabase || !businessId || !canView) return;

    const channel = supabase
      .channel(`active-worker-locations-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'location_updates',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          const row = payload.new as {
            resource_id?: string;
            activity?: string | null;
            speed?: number | null;
            odometer?: number | null;
            lat?: number | null;
            lng?: number | null;
            gps_timestamp?: string | null;
            created_at?: string | null;
          };
          if (!row?.resource_id) return;
          setLocations((prev) => {
            const next = new Map(prev);
            next.set(row.resource_id as string, {
              activity: row.activity,
              speed: row.speed,
              odometer: row.odometer,
              lat: row.lat,
              lng: row.lng,
              at: row.gps_timestamp || row.created_at || new Date().toISOString(),
            });
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [supabase, businessId, canView]);

  return {
    workers,
    activeJobs,
    locations,
    canView,
    loading,
    currentTime,
    refresh: load,
  };
}
